import React, { useEffect, useState } from 'react';
import { Heading, Typography } from '@fiverr-private/typography';
import { Form } from '@fiverr-private/forms';
import { Container, Stack } from '@fiverr-private/layout_components';
import {
    useIdentificationContext,
    PasswordHints,
    ForgetPasswordLink,
    PasswordInput,
    OnboardingFooter,
} from '@fiverr-private/identification_core';
import { appTranslate } from '../../i18n';
import { FormSubmitButton } from '../../shared/components/FormSubmitButton';
import { globalBigQueryReporter, globalGrafanaReporter, globalMixpanelReporter } from '../../events';
import { useStepsContext } from '../StepsContext';
import { Disclaimer } from '../Disclaimer';
import { useIdentificationStepForm } from './hooks/useIdentificationStepForm';
import { UsernameOrEmailInput } from './UsernameOrEmailInput';
import { useSubmitHandler } from './hooks/useSubmitHandler';
import { PASSWORD_VISIBLE_ERROR } from './EmailIdentificationStep.constants';

interface ButtonPayload {
    fullWidth: boolean;
}

export const EmailIdentificationStep = () => {
    const [loading, setLoading] = useState(false);
    const {
        isSignUp,
        methods,
        password,
        passwordError,
        onUsernameOrEmailChange,
        onPasswordVisibilityChange,
        usernameOrEmail,
    } = useIdentificationStepForm({ setLoading });
    const { variant } = useIdentificationContext();
    const isStandaloneOnboarding = variant === 'onboarding';
    const onSubmit = useSubmitHandler({ isSignUp, setError: methods.setError, setLoading });
    const { mode } = useIdentificationContext();
    const { goToStep, goBack } = useStepsContext();
    const isInitialSignUp = mode === 'sign_up';

    const title = appTranslate(
        isInitialSignUp ? 'steps.email_identification.title' : 'steps.email_identification.title_when_sign_in'
    );

    useEffect(() => {
        globalGrafanaReporter.count('identification.%{platform}.email_identification.show');
        globalMixpanelReporter.send('Identification - Email Identification - Show');
        globalBigQueryReporter.send('identification.email_identification.show');
    }, []);

    const getFormSubmitButton = ({ fullWidth }: ButtonPayload) => (
        <FormSubmitButton loading={loading} fullWidth={fullWidth}>
            {appTranslate(
                isSignUp
                    ? 'steps.email_identification.submit.sign_up_label'
                    : 'steps.email_identification.submit.sign_in_label'
            )}
        </FormSubmitButton>
    );

    return (
        <Container display="flex" direction="column" flexGrow="1" minHeight={0}>
            <Form minHeight={0} flexGrow="1" methods={methods} onSubmit={onSubmit}>
                <Stack
                    height="100%"
                    minHeight={0}
                    direction="column"
                    gap="0"
                    padding="px"
                    overflowY="auto"
                    maxWidth={isStandaloneOnboarding ? '550px' : undefined}
                >
                    <Heading marginBottom="8">
                        <Typography as={isStandaloneOnboarding ? 'h3' : 'h4'} color="black">
                            {title}
                        </Typography>

                        {isStandaloneOnboarding && (
                            <Typography fontSize="b_md" fontWeight="normal">
                                {appTranslate('steps.email_identification.onboarding_subtitle')}
                            </Typography>
                        )}
                    </Heading>
                    <Container display="flex" direction="column" gap="4" flexGrow="1">
                        <UsernameOrEmailInput loading={loading} onChange={onUsernameOrEmailChange} />
                        <Container>
                            <PasswordInput
                                name="password"
                                id="identification-password"
                                label={appTranslate<string>('fields.identification_password.label')}
                                autoComplete="current-password"
                                hideError={isSignUp || passwordError?.type !== PASSWORD_VISIBLE_ERROR}
                                onVisibilityChange={onPasswordVisibilityChange}
                            />
                            {isSignUp ? (
                                <Container marginTop="3">
                                    <PasswordHints password={password} />
                                </Container>
                            ) : (
                                <ForgetPasswordLink
                                    usernameOrEmail={usernameOrEmail}
                                    onResetPassword={() => goToStep('reset_password')}
                                />
                            )}
                        </Container>
                        {!isStandaloneOnboarding && (
                            <Container marginTop="auto">{getFormSubmitButton({ fullWidth: true })}</Container>
                        )}
                        {isStandaloneOnboarding && <Disclaimer />}
                    </Container>
                </Stack>

                {isStandaloneOnboarding && (
                    <Container marginTop="auto">
                        <OnboardingFooter goBack={goBack}>{getFormSubmitButton({ fullWidth: false })}</OnboardingFooter>
                    </Container>
                )}
            </Form>
        </Container>
    );
};
